import { useEffect, useState } from 'react';
import Modal from './Modal';
import Button from './Button';
import { Icon } from './Icon';
import { metadataApi } from '../../services/api';

/**
 * ColumnValuePicker — bir kolonun gercek CH degerlerinden secim.
 *
 * Admin filter satirinda "Değerden Seç" butonuna tiklar; bu modal CH'den
 * SELECT DISTINCT cekip gelen degerleri listeler. Admin liste ustunden
 * secim yapar — yazim hatasi sifir, eksikligi kendi gozuyle gorur.
 *
 * Filter op'una gore:
 *   - "IN" / "NOT IN" → coklu secim, virgullu string olarak filter.value'ya yazilir
 *   - "=" / "!=" / vs.  → tekli secim
 *
 * Props:
 *   open:    bool
 *   onClose: () => void
 *   table:   string — CH tablosu (ör. 'as_uretim')
 *   column:  string — kolon adi (ör. 'police_durumu_adi')
 *   op:      string — filter operator'u; coklu/tekli davranisi belirler
 *   current: string|array — mevcut filter.value (picker acilinca seçili gelir)
 *   onPick:  (value) => void — secim sonrasi cagirilir (op'a gore string veya array)
 */
export default function ColumnValuePicker({
  open,
  onClose,
  table,
  column,
  op = '=',
  current = '',
  onPick,
}) {
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());

  const isMulti = op === 'IN' || op === 'NOT IN';

  // Modal acildiginda degerleri yukle + current'tan baslangic secimi kur.
  useEffect(() => {
    if (!open || !table || !column) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    metadataApi
      .chColumnDistinct(table, column, 200)
      .then((res) => {
        if (cancelled) return;
        const vs = (res.data && res.data.values) || [];
        setValues(vs);
        setTruncated(Boolean(res.data && res.data.truncated));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err.message || 'Değerler getirilemedi. CH erişimi yok olabilir.',
        );
        setValues([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    // current'tan baslangic secimi
    if (isMulti) {
      let parts = [];
      if (Array.isArray(current)) parts = current;
      else if (typeof current === 'string' && current) {
        parts = current.split(',').map((v) => v.trim()).filter(Boolean);
      }
      setSelected(new Set(parts));
    } else {
      setSelected(new Set(current != null && current !== '' ? [String(current)] : []));
    }
    setSearch('');
    return () => {
      cancelled = true;
    };
  }, [open, table, column, isMulti, current]);

  const toggle = (v) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (isMulti) {
        if (next.has(v)) next.delete(v);
        else next.add(v);
      } else {
        // Tekli: yeni secim eskiyi siler
        next.clear();
        next.add(v);
      }
      return next;
    });
  };

  const handleApply = () => {
    const list = Array.from(selected);
    if (isMulti) {
      // IN / NOT IN için array olarak emit ediyoruz. Daha önce virgüllü
      // string yolluyorduk ve "backend split eder" diye yorum vardı; ama
      // backend `list(string)` çağırınca karakter karakter parçalıyordu
      // ("02-Kasko,03-Ferdi Kaza" → ['0','2','-','K',...]) ve SQL hiç
      // eşleşme bulamıyordu (rule_set 1 HYO görevi vakası, 2026-05-14).
      // Artık array yolluyoruz; backend defansif olarak hem array hem
      // string kabul ediyor (geriye dönük uyumluluk için).
      onPick(list);
    } else {
      onPick(list[0] || '');
    }
    onClose?.();
  };

  const filtered = search
    ? values.filter((v) => String(v).toLowerCase().includes(search.toLowerCase()))
    : values;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Değer Seç — ${column || ''}`}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            İptal
          </Button>
          <Button onClick={handleApply} disabled={selected.size === 0}>
            {isMulti
              ? `${selected.size} değeri uygula`
              : 'Seçili değeri uygula'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-xs text-neutral-600">
          <code className="bg-neutral-100 px-1 rounded">{table}.{column}</code>{' '}
          kolonunun veritabanındaki benzersiz değerleri.{' '}
          {isMulti ? (
            <span>
              Birden fazla değer seçebilirsin (<strong>{op}</strong> operatörü).
            </span>
          ) : (
            <span>
              Tek değer seç (<strong>{op}</strong> operatörü).
            </span>
          )}
        </p>

        <div className="flex items-center gap-2">
          <input
            type="text"
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Değer ara..."
            className="flex-1 rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
          {isMulti && !loading && filtered.length > 0 && (
            <button
              type="button"
              onClick={() => {
                const allSelected = filtered.every((v) => selected.has(String(v)));
                setSelected((prev) => {
                  const next = new Set(prev);
                  if (allSelected) {
                    // Tümünü kaldır (sadece görüntülenenler)
                    filtered.forEach((v) => next.delete(String(v)));
                  } else {
                    // Tümünü seç (görüntülenenler)
                    filtered.forEach((v) => next.add(String(v)));
                  }
                  return next;
                });
              }}
              className="whitespace-nowrap rounded-lg border border-neutral-200 px-3 py-2 text-xs text-neutral-600 hover:bg-neutral-50 transition-colors"
            >
              {filtered.every((v) => selected.has(String(v))) ? 'Tümünü Kaldır' : 'Tümünü Seç'}
            </button>
          )}
        </div>

        {loading && (
          <p className="text-sm text-neutral-500 text-center py-6">
            Veriler getiriliyor...
          </p>
        )}

        {error && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <Icon name="alert-triangle" className="inline h-4 w-4 mr-1" />
            {error}
            <p className="text-xs mt-1 text-amber-800">
              Değeri elle yazmak zorunda kalırsan, yazımın CH'deki gerçek
              değerle bire bir aynı olduğundan emin ol.
            </p>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <p className="text-sm text-neutral-500 text-center py-6">
            {search
              ? 'Arama sonucu boş.'
              : 'Bu kolonda henüz veri yok veya hepsi NULL.'}
          </p>
        )}

        <div className="divide-y divide-neutral-100 rounded-lg border border-neutral-200 max-h-[50vh] overflow-y-auto">
          {filtered.map((v, i) => {
            const key = String(v);
            const isSel = selected.has(key);
            return (
              <button
                key={i}
                type="button"
                onClick={() => toggle(key)}
                className={`w-full text-left px-3 py-2 hover:bg-primary-50/50 transition-colors flex items-center gap-2 ${
                  isSel ? 'bg-primary-50' : ''
                }`}
              >
                <span
                  className={`flex h-4 w-4 items-center justify-center rounded border ${
                    isSel
                      ? 'border-primary-500 bg-primary-500 text-white'
                      : 'border-neutral-300'
                  }`}
                >
                  {isSel && <Icon name="check" className="h-3 w-3" />}
                </span>
                <span className="text-sm text-neutral-800 truncate">{key}</span>
              </button>
            );
          })}
        </div>

        {truncated && (
          <p className="text-[11px] text-neutral-400">
            İlk 200 değer listeleniyor. Aradığın değer burada yoksa elle
            yazabilir veya filtreyi daraltmak için başka bir kolon seç.
          </p>
        )}
      </div>
    </Modal>
  );
}
